/**
 * Server-sent events over fetch, so the token can travel in a header.
 *
 * `EventSource` cannot send headers. The consent stream worked around that by
 * putting the access token in the query string - which the backend's auth
 * middleware never reads, so every connection was answered 401 and live consent
 * requests never arrived at all. It also meant a valid access token was written
 * into the reverse proxy's access log on every attempt, and into the browser's
 * own history.
 *
 * fetch can stream a response body and can carry an Authorization header, so
 * the token goes where every other request puts it and the URL stays clean.
 * What EventSource gave for free has to be written out here: frame parsing and
 * reconnection with backoff. In exchange, an expired token mid-stream is
 * repaired by the same refresh path as everything else rather than failing
 * silently forever.
 */

import { getAuthToken } from "@/utils/authToken";
import { refreshSession } from "@/utils/sessionRefresh";

export interface EventStreamHandlers {
  /** Called for each event. `type` is "message" unless the frame named one. */
  onEvent: (type: string, data: string) => void;
  onOpen?: () => void;
  /** Called when a connection attempt fails. The stream still retries. */
  onError?: (error: unknown) => void;
}

const FIRST_RETRY_MS = 1000;
const MAX_RETRY_MS = 30000;

/**
 * Parses one SSE frame - the text between blank lines.
 *
 * Returns null for a frame that carries no data: heartbeats arrive as bare
 * comment lines (": heartbeat"), and delivering those as empty events would
 * have every listener parsing "" as JSON every thirty seconds.
 */
function parseFrame(frame: string): { type: string; data: string } | null {
  let type = "message";
  const dataLines: string[] = [];

  for (const line of frame.split("\n")) {
    if (!line || line.startsWith(":")) continue;

    const colon = line.indexOf(":");
    const field = colon === -1 ? line : line.slice(0, colon);
    // One optional space after the colon is part of the framing, not the value.
    let value = colon === -1 ? "" : line.slice(colon + 1);
    if (value.startsWith(" ")) value = value.slice(1);

    if (field === "event") type = value;
    else if (field === "data") dataLines.push(value);
  }

  if (dataLines.length === 0) return null;
  return { type, data: dataLines.join("\n") };
}

/**
 * Opens the stream and keeps it open. Returns a function that closes it and
 * stops it reconnecting.
 */
export function openEventStream(
  url: string,
  handlers: EventStreamHandlers,
): () => void {
  const controller = new AbortController();
  let closed = false;
  let retryDelay = FIRST_RETRY_MS;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleRetry = () => {
    if (closed) return;
    retryTimer = setTimeout(() => void connect(), retryDelay);
    // Backing off matters here: a backend that is down would otherwise get a
    // reconnect every second from every open tab.
    retryDelay = Math.min(retryDelay * 2, MAX_RETRY_MS);
  };

  async function connect(attemptedRefresh = false): Promise<void> {
    if (closed) return;

    const token = getAuthToken();
    if (!token) {
      scheduleRetry();
      return;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        signal: controller.signal,
      });
    } catch (error) {
      if (closed) return;
      handlers.onError?.(error);
      scheduleRetry();
      return;
    }

    // The token expired while the stream was open, or between attempts. One
    // refresh, then try again; anything more would loop against a dead session.
    if (response.status === 401 && !attemptedRefresh) {
      const refreshed = await refreshSession();
      if (refreshed) return connect(true);
    }

    if (!response.ok || !response.body) {
      handlers.onError?.(
        new Error(`Event stream refused with status ${response.status}`),
      );
      scheduleRetry();
      return;
    }

    // Connected. Reset the backoff so a later blip starts from one second.
    retryDelay = FIRST_RETRY_MS;
    handlers.onOpen?.();

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Frames are separated by a blank line. A chunk can end mid-frame, so
        // whatever follows the last separator stays in the buffer.
        let separator = buffer.indexOf("\n\n");
        while (separator !== -1) {
          const frame = buffer.slice(0, separator);
          buffer = buffer.slice(separator + 2);

          const parsed = parseFrame(frame);
          if (parsed) handlers.onEvent(parsed.type, parsed.data);

          separator = buffer.indexOf("\n\n");
        }
      }
    } catch (error) {
      if (!closed) handlers.onError?.(error);
    }

    // The server closed the stream, or the network went away. Either way it
    // has to be reopened - this is what EventSource did on its own.
    if (!closed) scheduleRetry();
  }

  void connect();

  return () => {
    closed = true;
    if (retryTimer) clearTimeout(retryTimer);
    controller.abort();
  };
}
